<template>
  <validation-provider
    :immediate="immediate"
    :rules="rules"
    :name="name"
    :vid="name"
    v-slot="validation"
    tag="div"
  >
    <div
      :class="{
        [$style.container]: true,
        [$style.hasContent]: hasContent,
        [$style.disabled]: disabled,
        [$style.hasIcon]: Boolean(icon),
        [$style.noFocus]: noFocus,
        [$style.invalid]: validation.valid === false,
        [$style.valid]: validation.valid === true
      }"
    >
      <label :class="$style.label" :for="idFromName">{{ label }}</label>
      <div :class="$style.inputWrapper">
        <input
          :class="$style.input"
          v-model="innerValue"
          :placeholder="placeholder"
          :autofocus="autofocus"
          :disabled="disabled"
          :type="type"
          :name="name"
          :min="min"
          :max="max"
          :id="idFromName"
          ref="input"
          autocapitalize="off"
          :autocomplete="autocomplete"
          autocorrect="off"
          spellcheck="false"
          data-lpignore="true"
          @animationstart="checkAnimation"
        />
        <div :class="$style.iconContainer" v-if="icon">
          <button
            v-if="iconClick"
            :class="$style.iconButton"
            type="button"
            @click="iconClick"
          >
            <base-icon
              :name="icon"
              :preload="preloadIcons"
              themeSize="x-large"
              themeColor="icons"
            />
          </button>
          <base-icon
            v-else
            :name="icon"
            :preload="preloadIcons"
            themeSize="x-large"
            themeColor="icons"
            :class="$style.icon"
          />
        </div>
      </div>
      <div :class="$style.error">{{ validation.errors[0] }}</div>
    </div>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import { kebabCase } from 'lodash'

export default {
  name: 'base-text-input',

  components: { ValidationProvider },

  props: {
    autocomplete: { String, default: 'off' },
    preloadIcons: { type: Array },
    placeholder: { String },
    autofocus: { Boolean, default: false },
    iconClick: { Function },
    immediate: { Boolean },
    disabled: { Boolean, default: false },
    noFocus: { Boolean },
    label: { String, required: true },
    value: { String },
    rules: { String },
    type: { String, default: 'text' },
    name: { String, required: true },
    icon: { String },
    min: { Number },
    max: { Number }
  },

  data: () => ({
    autofilled: false,
    innerValue: null
  }),

  computed: {
    hasContent() {
      return (
        (this.value && this.value.toString().length > 0) ||
        this.autofilled ||
        this.value === 0
      )
    },

    idFromName() {
      return kebabCase(this.name)
    }
  },

  watch: {
    innerValue(val) {
      this.$emit('input', val)
    },

    value(newVal) {
      // required to handle value updates from outside
      this.innerValue = newVal
    }
  },

  methods: {
    checkAnimation(e) {
      if (e.animationName === 'onAutoFillStart') {
        this.autofilled = true
      } else if (e.animationName === 'onAutoFillCancel') {
        this.autofilled = false
      }
    }
  },

  created() {
    // required to prefill with initial value
    this.innerValue = this.value
  }
}
</script>

<style lang="scss" module>
.container {
  margin-bottom: spacing(2);
  position: relative;
}

.label {
  display: block;
  font-family: $font-primary;
  line-height: 1.5;
  position: absolute;
  left: spacing(2);
  transition: 0.2s;
  z-index: 1;
  pointer-events: none;

  // clean
  color: $color-labels;
  font-size: $size-medium;
  font-weight: $weight-regular;
  top: spacing(2);

  .container.disabled & {
    opacity: 0.6;
  }

  .container:focus-within &,
  .container.hasContent & {
    color: $color-primary-light;
    font-size: $size-xx-small;
    font-weight: $weight-medium;
    letter-spacing: 0.4px;
    top: spacing(1);
  }
}

.inputWrapper {
  position: relative;
}

.input {
  background: $color-fields;
  border: none;
  width: 100%;
  display: block;
  color: $color-copy;
  font-family: $font-primary;
  font-size: $size-medium;
  font-weight: $weight-regular;
  line-height: 1.5;
  outline: none;
  padding: spacing(3) spacing(2) spacing(1) spacing(2);
  box-shadow: 0 -3px 0 -2px #c2c4c6 inset;

  .hasIcon & {
    padding-right: spacing(6);
  }

  .hasContent &,
  &:focus {
    background-color: rgba(0, 143, 202, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .invalid:not(.noFocus) & {
    box-shadow: 0 -4px 0 -2px $color-danger inset;
  }

  .valid:not(.noFocus) & {
    box-shadow: 0 -4px 0 -2px $color-success inset;
  }
}

.iconContainer {
  position: absolute;
  bottom: spacing(2);
  right: 12px;
}

.icon {
  margin: 0;
  width: 24px;
}

.iconButton {
  @include reset-btn;
  vertical-align: middle;
  padding: 8px;
  margin: -8px;
  outline: none;

  &:focus {
    outline: 1px dashed $color-borders;
  }

  &:hover svg,
  &:focus svg {
    fill: $color-primary-light !important;
  }
}

.error {
  font-size: $size-xx-small;
  font-weight: $weight-medium;
  line-height: $size-medium;
  letter-spacing: 0.4px;
  color: $color-danger;
  min-height: spacing(2);
  margin-left: spacing(2);
  margin-top: 2px;
  text-align: left;
}
</style>
